import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, MapPin, Flag, Clock, Radio, CalendarCheck, AlertTriangle } from "lucide-react";
import api from "../../lib/api";

/* Group coordination: who's arrived / en route / delayed + shared meeting point. */
const STATUS_TONE = {
  arrived: "bg-emerald-50 text-emerald-700 border-emerald-200",
  en_route: "bg-blue-50 text-blue-700 border-blue-200",
  delayed: "bg-amber-50 text-amber-800 border-amber-300",
  wait_for_me: "bg-orange-50 text-orange-700 border-orange-200",
  meet_you_there: "bg-indigo-50 text-indigo-700 border-indigo-200",
  separated: "bg-rose-50 text-rose-700 border-rose-200",
  returned: "bg-teal-50 text-teal-700 border-teal-200",
  safe: "bg-emerald-50 text-emerald-700 border-emerald-200",
  not_started: "bg-zinc-50 text-zinc-500 border-zinc-200",
};
const QUICK = [["en_route", "I'm on my way"], ["arrived", "Arrived"], ["delayed", "Delayed"],
  ["wait_for_me", "Wait for me"], ["meet_you_there", "I'll meet you there"], ["safe", "I'm safe"]];

const CI_TONE = {
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  scheduled: "bg-zinc-50 text-zinc-500 border-zinc-200",
  due: "bg-amber-50 text-amber-800 border-amber-300",
  escalated: "bg-rose-50 text-rose-700 border-rose-300",
};

/* Organizer-only: every traveler's check-ins for this circle in one place. */
function CheckinBoard({ cid }) {
  const [board, setBoard] = useState(null);
  useEffect(() => {
    api.get(`/guardian/circles/${cid}/checkins-board`).then((r) => setBoard(r.data)).catch(() => setBoard(null));
  }, [cid]);
  if (!board) return null;
  const S = board.summary;
  return (
    <div className="mt-6 border border-zinc-200 bg-white rounded-2xl p-4" data-testid="guardian-checkin-board">
      <div className="flex items-center gap-2 mb-3">
        <CalendarCheck className="w-4 h-4 text-emerald-600" />
        <span className="text-sm font-800">Check-in board</span>
        <span className="text-[10px] text-zinc-400 uppercase">organizer view</span>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-4 text-center">
        {[["Travelers", S.travelers, ""], ["Need attention", S.attention, S.attention ? "text-rose-600" : ""],
          ["Upcoming", S.upcoming, ""], ["Done (7d)", S.completed_7d, "text-emerald-700"]].map(([k, v, c]) => (
          <div key={k} className="border border-zinc-200 rounded-xl py-2">
            <div className={`font-900 text-lg ${c}`}>{v}</div>
            <div className="text-[10px] text-zinc-400 font-600">{k}</div>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        {board.rows.map((r) => (
          <div key={r.user_id || r.name} data-testid="guardian-board-row"
            className={`border rounded-xl px-4 py-2.5 ${r.attention ? "border-rose-300 bg-rose-50/40" : "border-zinc-200"}`}>
            <div className="flex items-center gap-2 flex-wrap">
              {r.attention && <AlertTriangle className="w-4 h-4 text-rose-500" />}
              <span className="text-sm font-700">{r.name}</span>
              <span className="text-[10px] text-zinc-400 uppercase">{(r.role || "").replace(/_/g, " ")}</span>
              {r.active_sessions > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] font-800 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                  <Radio className="w-3 h-3" /> sharing live
                </span>
              )}
            </div>
            {r.checkins.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {r.checkins.map((c) => (
                  <span key={c.id} className={`inline-flex items-center gap-1 border rounded-full px-2.5 py-0.5 text-[10px] font-700 ${CI_TONE[c.status] || CI_TONE.scheduled}`}>
                    {c.label} · {new Date(c.scheduled_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} · {c.status}
                  </span>
                ))}
              </div>
            ) : <p className="text-[11px] text-zinc-400 mt-1">No check-ins scheduled for this circle.</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function GuardianGroup() {
  const [circles, setCircles] = useState([]);
  const [cid, setCid] = useState("");
  const [data, setData] = useState(null);
  const [mp, setMp] = useState({ label: "", when: "" });
  const [editMp, setEditMp] = useState(false);

  useEffect(() => {
    api.get("/guardian/circles").then((r) => {
      setCircles(r.data.items);
      if (r.data.items[0]) setCid((c) => c || r.data.items[0].id);
    }).catch(() => {});
  }, []);

  const load = (id) => api.get(`/guardian/circles/${id}/group`).then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { if (cid) load(cid); }, [cid]);

  const saveMp = async () => {
    try {
      await api.put(`/guardian/circles/${cid}/meeting-point`, mp);
      toast.success("Meeting point shared with the group"); setEditMp(false); load(cid);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not set meeting point"); }
  };
  const myStatus = async (status) => {
    try { await api.post(`/guardian/circles/${cid}/my-status`, { status }); toast.success("Status updated"); load(cid); }
    catch { toast.error("Could not update"); }
  };

  if (circles.length === 0) return <p className="text-sm text-zinc-400 border border-dashed border-zinc-300 rounded-xl px-4 py-8 text-center" data-testid="guardian-group-empty">Create a circle first — the group board lives on top of your circles.</p>;

  return (
    <div data-testid="guardian-group">
      <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid="guardian-group-circle"
        className="border border-zinc-300 rounded-xl px-3 py-2.5 text-sm bg-white mb-4">
        {circles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>

      {data && (
        <>
          {/* Meeting point */}
          <div className="border border-indigo-200 bg-indigo-50/50 rounded-2xl p-4 mb-4" data-testid="guardian-meeting-point">
            <div className="flex items-center gap-2">
              <Flag className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-800">Meeting point</span>
              {(data.my_role === "owner" || data.my_role === "trip_coordinator") && (
                <button onClick={() => { setEditMp(!editMp); setMp({ label: data.meeting_point?.label || "", when: data.meeting_point?.when || "" }); }}
                  data-testid="guardian-mp-edit" className="ml-auto text-xs font-800 text-indigo-700 hover:underline">{data.meeting_point ? "Change" : "Set"}</button>
              )}
            </div>
            {data.meeting_point ? (
              <p className="mt-1.5 text-sm text-zinc-700"><MapPin className="w-3.5 h-3.5 inline text-indigo-500" /> <b>{data.meeting_point.label}</b>
                {data.meeting_point.when && <span className="text-zinc-500"> · <Clock className="w-3 h-3 inline" /> {data.meeting_point.when}</span>}
                <span className="text-[10px] text-zinc-400"> — set by {data.meeting_point.set_by}</span></p>
            ) : <p className="mt-1.5 text-xs text-zinc-500">No meeting point set yet.</p>}
            {editMp && (
              <div className="mt-3 grid sm:grid-cols-3 gap-2">
                <input value={mp.label} onChange={(e) => setMp({ ...mp, label: e.target.value })} data-testid="guardian-mp-label"
                  placeholder="Where (e.g. Terminal 3, Gate A - Costa Coffee)" className="border border-zinc-300 rounded-lg px-3 py-2 text-sm sm:col-span-2" />
                <input value={mp.when} onChange={(e) => setMp({ ...mp, when: e.target.value })} data-testid="guardian-mp-when"
                  placeholder="When (e.g. 6:30 PM)" className="border border-zinc-300 rounded-lg px-3 py-2 text-sm" />
                <button onClick={saveMp} data-testid="guardian-mp-save" className="bg-indigo-600 text-white text-xs font-800 rounded-full px-4 py-2 hover:bg-indigo-700 w-fit">Share with group</button>
              </div>
            )}
          </div>

          {/* My quick status */}
          <div className="mb-4">
            <div className="text-[11px] font-800 uppercase tracking-wide text-zinc-400 mb-1.5">My status</div>
            <div className="flex flex-wrap gap-2" data-testid="guardian-group-quick">
              {QUICK.map(([v, l]) => (
                <button key={v} onClick={() => myStatus(v)} data-testid={`guardian-group-status-${v}`}
                  className="border border-zinc-300 bg-white text-zinc-700 text-xs font-800 rounded-full px-3.5 py-2 hover:border-blue-500 hover:text-blue-700">{l}</button>
              ))}
            </div>
          </div>

          {/* Board */}
          <div className="space-y-2" data-testid="guardian-group-board">
            {data.people.map((p) => (
              <div key={p.user_id || p.name} className="border border-zinc-200 bg-white rounded-xl px-4 py-2.5 flex items-center gap-3" data-testid="guardian-group-row">
                <Users className="w-4 h-4 text-zinc-400 shrink-0" />
                <span className="text-sm font-700">{p.name}</span>
                <span className="text-[10px] text-zinc-400 uppercase">{p.role.replace("_", " ")}</span>
                <span className={`ml-auto rounded-full border px-2.5 py-0.5 text-[10px] font-800 uppercase ${STATUS_TONE[p.status]}`}>{data.status_labels[p.status]}</span>
                {p.updated_at && <span className="text-[10px] text-zinc-400">{new Date(p.updated_at).toLocaleTimeString()}</span>}
              </div>
            ))}
          </div>

          {/* Organizer check-in board */}
          {(data.my_role === "owner" || data.my_role === "trip_coordinator") && <CheckinBoard cid={cid} />}
        </>
      )}
    </div>
  );
}
